
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { ExamResult, ExamType } from '@/utils/exam/types';
import { addExamResult, updateExamResult, getStudentExams } from '@/services/exam';
import { cleanScoreValue, detectPercentageAndConvert, calculatePercentage } from '@/utils/validation/examValidation';
import { dbService } from '@/services/db';

export const useExamResultForm = () => {
  const navigate = useNavigate();
  const { studentId, examId } = useParams();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialExam, setInitialExam] = useState<ExamResult | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [studentPrograms, setStudentPrograms] = useState<any[]>([]);
  
  const isEditMode = !!examId;
  const { state } = location;

  const { 
    register, 
    handleSubmit: hookHandleSubmit, 
    formState: { errors, touchedFields }, 
    setValue, 
    watch,
    reset,
    trigger
  } = useForm<Partial<ExamResult>>({
    defaultValues: {
      examType: 'test',
      examDate: format(new Date(), 'yyyy-MM-dd'),
      maxScore: 100,
      obtainedScore: 0,
    },
    mode: 'onSubmit' // Only validate on form submission
  });

  // Watch score values for percentage calculation
  const watchObtainedScore = watch('obtainedScore', 0);
  const watchMaxScore = watch('maxScore', 100);
  const watchProgramTitle = watch('programTitle', '');
  
  // Calculate percentage from watched values with improved handling of percentage input
  const obtainedScoreValue = cleanScoreValue(watchObtainedScore);
  const maxScoreValue = cleanScoreValue(watchMaxScore);
  const percentage = maxScoreValue > 0 ? ((obtainedScoreValue / maxScoreValue) * 100).toFixed(1) : '0.0';

  // Create a custom handleSubmit that sets formSubmitted to true
  const handleSubmit = (onValid: (data: Partial<ExamResult>) => void) => {
    return hookHandleSubmit((data) => {
      setFormSubmitted(true);
      onValid(data);
    });
  };

  // Fetch student programs for auto-populating
  useEffect(() => {
    if (studentId) {
      const fetchStudentData = async () => {
        try {
          const student = await dbService.getStudent(Number(studentId));
          if (student && student.programs) {
            setStudentPrograms(student.programs);
            
            // If this is a new exam and we have programs, set the first program as default
            if (!isEditMode && student.programs.length > 0 && !watchProgramTitle) {
              setValue('programTitle', student.programs[0].title);
            }
          }
        } catch (error) {
          console.error('Error fetching student programs:', error);
        }
      };
      
      fetchStudentData();
    }
  }, [studentId, isEditMode, setValue, watchProgramTitle]);

  useEffect(() => {
    // Ensure maxScore is set to 100 when the component loads
    setValue('maxScore', 100);
    
    const loadExam = async () => {
      if (isEditMode && studentId && examId) {
        try {
          const results = await getStudentExams(studentId);
          const exam = results.find(e => e.id === examId);
          
          if (exam) {
            // Convert the exam to the expected type format
            const typedExam: ExamResult = {
              ...exam,
              createdAt: typeof exam.createdAt === 'object' 
                ? exam.createdAt.toISOString() 
                : exam.createdAt,
              examDate: typeof exam.examDate === 'object'
                ? exam.examDate.toISOString()
                : exam.examDate
            };
            
            setInitialExam(typedExam);
            reset({
              ...typedExam,
              examDate: format(new Date(typedExam.examDate), 'yyyy-MM-dd'),
              maxScore: typedExam.maxScore || 100,
              obtainedScore: typedExam.obtainedScore || 0, // Ensure we have a default value
            });
          } else {
            toast.error("Exam not found");
            navigate(`/students/${studentId}`);
          }
        } catch (error) {
          console.error("Error loading exam:", error);
          toast.error("Failed to load exam details");
        }
      } else if (state?.examData) {
        const examData = {
          ...state.examData,
          maxScore: state.examData.maxScore || 100,
          obtainedScore: state.examData.obtainedScore || 0, // Ensure default value
        };
        reset(examData);
      }
    };

    loadExam();
  }, [isEditMode, studentId, examId, reset, navigate, state, setValue]);
  
  // Update percentage calculation whenever scores change
  useEffect(() => {
    // This effect keeps the percentage updated in real-time
    const obtainedValue = cleanScoreValue(watchObtainedScore);
    const maxValue = cleanScoreValue(watchMaxScore);
    
    // Fix the TypeScript error by converting to string first
    const obtainedScoreStr = String(watchObtainedScore);
    if (obtainedScoreStr.includes('%')) {
      const calculatedScore = detectPercentageAndConvert(obtainedScoreStr, maxValue);
      setValue('obtainedScore', calculatedScore);
    }
  }, [watchObtainedScore, watchMaxScore, setValue]);
  
  const prepareFormData = (data: any) => {
    // Create a copy of the data to avoid mutating the original
    let formData = { ...data };
    
    // Handle obtainedScore with percentage detection
    if (formData.obtainedScore !== undefined) {
      formData.obtainedScore = detectPercentageAndConvert(
        String(formData.obtainedScore), 
        formData.maxScore || 100
      );
    }
    
    // Handle maxScore with safer type checking
    if (formData.maxScore !== undefined) {
      formData.maxScore = cleanScoreValue(formData.maxScore);
    }
    
    // Ensure scores are numeric and not undefined
    formData.obtainedScore = Number(formData.obtainedScore || 0);
    formData.maxScore = Number(formData.maxScore || 100);
    
    console.log('Prepared form data:', formData); // Debug log
    return formData;
  };

  const onSubmit = async (data: Partial<ExamResult>) => {
    if (!studentId) {
      toast.error("Student ID is missing");
      return;
    }

    // Prepare form data
    const cleanedData = prepareFormData(data);

    setIsSubmitting(true);
    try {
      let success = false;
      
      if (isEditMode && examId) {
        success = await updateExamResult(studentId, examId, cleanedData);
        if (success) {
          toast.success("Exam result updated successfully");
        }
      } else {
        success = await addExamResult(studentId, cleanedData as Omit<ExamResult, 'id' | 'createdAt'>);
        if (success) {
          toast.success("Exam result added successfully");
        }
      }
      
      if (success) {
        navigate(`/students/${studentId}`);
      }
    } catch (error) {
      console.error('Error saving exam:', error);
      toast.error('Failed to save exam result');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/students/${studentId}`);
  };

  return {
    register,
    errors,
    touchedFields,
    formSubmitted,
    handleSubmit,
    setValue,
    watch,
    onSubmit,
    handleCancel,
    isSubmitting,
    isEditMode,
    watchObtainedScore,
    watchMaxScore,
    watchProgramTitle,
    percentage,
    studentId,
    studentPrograms
  };
};

export const examTypes = [
  { value: 'monthly', label: 'Monthly Exam' },
  { value: 'annual', label: 'Annual Exam' },
  { value: 'test', label: 'Test' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'other', label: 'Other' },
] as const;
